import { useState } from 'react';
import { Loader2, LogIn, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useSessionStatus } from '@/context/AuthSessionContext';
import { useAuth } from '@/hooks/useAuth';
import { clearLocalToken } from '@/auth/localSession';
import { resetAuthMe } from '@/auth/authMeStore';
import { clearStaleMsalInteraction, clearInteractiveRetry } from '@/auth/msalRecovery';
import { t } from '@/i18n';

export function SessionExpiredModal() {
  const { sessionExpired, setSessionExpired } = useSessionStatus();
  const { login, isLocalSession } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleReLogin = async () => {
    setLoading(true);
    try {
      if (isLocalSession) {
        // Local JWTs are not refreshable — clear the session and return to
        // the login page.
        resetAuthMe();
        clearLocalToken();
        setSessionExpired(false);
        window.location.assign('/');
        return;
      }
      // Entra: full-page redirect to sign in again (MSAL v5 popup flows
      // require a COOP redirect bridge; redirect flows do not).
      //
      // 2026-09-07: login() itself clears MSAL's stale interaction state
      // first — a leftover `msal.interaction.status` used to make every
      // later redirect throw interaction_in_progress, which is why a
      // re-login in THIS tab looked successful and the modal came right
      // back while a fresh tab worked. Belt-and-braces here too, since this
      // button is the user's last resort. There is deliberately NO
      // setSessionExpired(false) after login(): loginRedirect navigates the
      // page away, so that line never ran — the latch is now cleared by the
      // first successful authenticated request (useApi.requestWithAuth).
      clearStaleMsalInteraction();
      await login();
    } catch (error) {
      // Redirect init failed (blocked navigation, MSAL still mid-interaction).
      // Say so loudly and offer the hard reset instead of leaving a dead
      // button behind.
      console.error('[Auth] Re-login failed to start:', error);
      toast.error(t('errors.signInFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Hard reset: a full page load rebuilds MSAL state from scratch, which is
  // what "open it in a fresh tab" did manually during the 2026-09-07 incident.
  //
  // A reload alone is NOT equivalent to a fresh tab: sessionStorage survives
  // it, so BOTH pieces of per-tab state have to be dropped explicitly — MSAL's
  // pending-interaction bookkeeping AND our own once-only fallback latch.
  // Leaving the latch armed would let the reloaded page hit its first silent
  // renewal, skip the redirect fallback, and re-open this very modal.
  const handleReload = () => {
    clearStaleMsalInteraction();
    clearInteractiveRetry();
    window.location.assign('/');
  };

  return (
    <AlertDialog open={sessionExpired}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('errors.sessionExpired')}</AlertDialogTitle>
          <AlertDialogDescription>{t('errors.sessionExpiredMessage')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" disabled={loading} onClick={handleReload}>
            <RefreshCw className="size-4" />
            {t('errors.reloadPage')}
          </Button>
          <Button disabled={loading} onClick={handleReLogin}>
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <LogIn className="size-4" />
            )}
            {t('errors.signInAgain')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
